import React, { useState, useRef, useEffect } from 'react';
import i18next from 'i18next';
import clsx from 'clsx';

import axiosInstance from '../../config/axiosConfig';

import useSelectedCompanyStore from '../../store/useSelectedCompanyStore';
import useUserStore from '../../store/useUserStore';

import countries from 'i18n-iso-countries';
import en from 'i18n-iso-countries/langs/en.json';

import { FileUpload } from 'primereact/fileupload';
import { Toast } from 'primereact/toast';
import { ProgressSpinner } from 'primereact/progressspinner';
import { Avatar } from 'primereact/avatar';
import { ToggleButton } from 'primereact/togglebutton';
import { Dropdown } from 'primereact/dropdown';
import { InputText } from 'primereact/inputtext';
import { Tooltip } from 'primereact/tooltip';

countries.registerLocale(en);
let allCountries = Object.values(countries.getNames('en', { select: 'official' }));

const t = (i18nKey, params) => i18next.t(`userDashboard.company.general_info.${i18nKey}`, params);

const GeneralInfo = () => {
    const selectedCompany = useSelectedCompanyStore((state) => state.selectedCompany);
    const setSelectedCompany = useSelectedCompanyStore((state) => state.setSelectedCompany);

    const companies = useUserStore((state) => state.companies);
    const setCompanies = useUserStore((state) => state.setCompanies);

    const [selectedCompanyDefault, setSelectedCompanyDefault] = useState(selectedCompany);
    const [companyDetails, setCompanyDetails] = useState({});
    const [isUploading, setIsUploading] = useState(false);
    const [uploadedImage, setUploadedImage] = useState(selectedCompany?.profile_picture ? `/dashboard/company/avatar/${selectedCompany?.profile_picture}` : '/images/dashboard/400x400.png');
    const [uploadDetails, setUploadDetails] = useState('');
    const [isUpdating, setIsUpdating] = useState(false);
    const [isLoading, setIsLoading] = useState(true);

    const toast = useRef(null);

    useEffect(() => {
        const fetchCompanyDetails = async () => {
            try {
                const response = await axiosInstance.get(`/dashboard/companies/${selectedCompany?.company_id}/getDetails`);
                setCompanyDetails(response.data);
                setSelectedCompanyDefault(response.data.company);
                setSelectedCompany(response.data.company);
            } catch (error) {
                toast.current.show({ severity: 'error', summary: t('fetch_error'), life: 5000 });
            } finally {
                setIsLoading(false);
            }
        };

        fetchCompanyDetails();
    }, [selectedCompany?.company_id]);

    const validateFile = (file) => {
        const allowedTypes = ['image/jpeg', 'image/png', 'image/gif'];
        if (file.size > 2 * 1024 * 1024) {
            toast.current.show({ severity: 'error', summary: t('image_size_error'), life: 5000 });
            return false;
        }
        if (!allowedTypes.includes(file.type)) {
            toast.current.show({ severity: 'error', summary: t('file_error'), life: 5000 });
            return false;
        }
        return true;
    };

    const processImage = (file, callback) => {
        const reader = new FileReader();
        reader.onload = (event) => {
            const img = new Image();
            img.onload = () => {
                if (img.width < 200 || img.height < 200) {
                    toast.current.show({ severity: 'error', summary: t('image_dimensions_error'), life: 5000 });
                    return;
                }

                const canvas = document.createElement('canvas');
                const ctx = canvas.getContext('2d');
                if (img.width < 400 || img.height < 400) {
                    const newWidth = Math.max(img.width, 400);
                    const newHeight = Math.max(img.height, 400);
                    canvas.width = newWidth;
                    canvas.height = newHeight;
                    ctx.fillStyle = 'white';
                    ctx.fillRect(0, 0, newWidth, newHeight);
                    ctx.drawImage(img, (newWidth - img.width) / 2, (newHeight - img.height) / 2);
                } else {
                    canvas.width = 400;
                    canvas.height = 400;
                    const sx = img.width / 2 - 200;
                    const sy = img.height / 2 - 200;
                    ctx.drawImage(img, sx, sy, 400, 400, 0, 0, 400, 400);
                }

                const resizedImageUrl = canvas.toDataURL('image/jpeg');
                callback(resizedImageUrl);
            };
            img.src = event.target.result;
        };
        reader.readAsDataURL(file);
    };

    const uploadImage = async (file) => {
        setIsLoading(true);
        const formData = new FormData();
        formData.append('avatar', file);

        try {
            setIsUploading(true);
            const response = await axiosInstance.post(`/dashboard/company/${selectedCompany?.company_id}/profile-picture`, formData, {
                headers: { 'Content-Type': 'multipart/form-data' },
            });

            setIsUploading(false);

            const updatedCompany = { ...selectedCompanyDefault, profile_picture: response.data?.avatar };
            setSelectedCompanyDefault(updatedCompany);

            setCompanies(companies?.map(company => {
                if (company.company_id === selectedCompany?.company_id) {
                    return { ...company, profile_picture: response.data?.avatar };
                }
                return company;
            }));

            setUploadDetails('');

            toast.current.show({ severity: 'success', summary: t('image_success'), life: 5000, detail: t('image_success_detail') });
        } catch (error) {
            setIsUploading(false);
            console.log('error', error);
            toast.current.show({ severity: 'error', summary: t('upload_error'), life: 5000 });
        } finally {
            setIsLoading(false);
        }
    };

    const handleFileUpload = (file) => {
        if (!validateFile(file)) return;
        processImage(file, (resizedImageUrl) => {
            setUploadedImage(resizedImageUrl);
            setUploadDetails(`${file.name} - ${(file.size / (1024 * 1024)).toFixed(2)}MB`);
            uploadImage(file);
        });
    };

    const handleDrop = (e) => {
        e.preventDefault();
        e.currentTarget.style.backgroundColor = '';
        const file = e.dataTransfer.files[0];
        if (file) handleFileUpload(file);
    };

    const handleDragOver = (e) => {
        e.preventDefault();
        e.currentTarget.style.backgroundColor = '#e0e0e0';
    };

    const handleDragLeave = (e) => {
        e.preventDefault();
        e.currentTarget.style.backgroundColor = '';
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setSelectedCompanyDefault({
            ...selectedCompanyDefault,
            [name]: value,
        });
    };

    const handleUpdate = async () => {
        try {
            setIsLoading(true);
            setIsUpdating(true);
            await axiosInstance.put(`/dashboard/companies/${selectedCompany?.company_id}/update`, {
                ...selectedCompanyDefault
            });

            setCompanies(companies?.map(company => {
                if (company.company_id === selectedCompany?.company_id) {
                    return { ...company, ...selectedCompanyDefault };
                }
                return company;
            }));

            toast.current.show({ severity: 'success', summary: t('updated'), life: 5000 });
            setIsUpdating(false);
        } catch (error) {
            toast.current.show({ severity: 'error', summary: t('update_error'), life: 5000 });
            setIsUpdating(false);
        } finally {
            setIsLoading(false);
        }
    };

    const sizeOptions = [
        { label: '1-20', value: '1-20' },
        { label: '20-50', value: '20-50' },
        { label: '50-100', value: '50-100' },
        { label: '100-200', value: '100-200' },
        { label: '200-500', value: '200-500' },
        { label: '+500', value: '+500' }
    ];

    return (
        <div>
            <Toast ref={toast} position='bottom-right' />

            <Tooltip
                target=".company_is_verified_true"
                content="Cette entreprise a été vérifié par DeePsy, vous ne pouvez donc plus les modifier ces informations, si devez mettre à jour vos informations, merci de nous contacter."
                position="top"
                mouseTrack
                style={{ maxWidth: 500 }}
                pt={{
                    arrow: {
                        style: { display: 'none' }
                    }
                }}
            />

            <div className="columns is-vcentered">
                <div className="column is-narrow">
                    {isLoading ? (
                        <ProgressSpinner style={{ width: '50px', height: '50px' }} strokeWidth="4" />
                    ) : (
                        // <Avatar
                        //     image={companyDetails.owner?.profile_picture || undefined}
                        //     label={!companyDetails.owner?.profile_picture ? companyDetails.owner?.first_name[0]?.toUpperCase() : undefined}
                        //     shape="circle"
                        //     size="xlarge"
                        // />

                        <>
                            {companyDetails.owner?.profile_picture ? (
                                <Avatar
                                    image={`/dashboard/user/avatar/${companyDetails.owner?.profile_picture}`}
                                    size="large"
                                    shape="xlarge"
                                />
                            ) : (
                                <div className="deepsy-anonymous-avatar" style={{ width: '3.5rem', height: '3.5rem', fontSize: '2rem' }}>
                                    <span className='has-text-dgrad-deepsy'>{companyDetails.owner?.first_name[0].toUpperCase()}{companyDetails.owner?.last_name[0].toUpperCase()}</span>
                                </div>
                            )}
                        </>
                    )}
                </div>

                <div className="column pb-0">
                    {isLoading ? (
                        <ProgressSpinner style={{ width: '20px', height: '20px' }} strokeWidth="2" />
                    ) : (
                        <h1 className="title is-capitalized has-text-dgrad-deepsy">
                            {companyDetails.owner?.display_name ?? `${companyDetails.owner?.first_name || 'Chargement...'} ${companyDetails.owner?.last_name || ''}`}
                        </h1>
                    )}
                </div>
            </div>

            <div className="columns">
                <div className="column">
                    <div className="field">
                        <label className="label">{t('company_name')}
                            {!!selectedCompany?.is_verified && <i className="pi pi-info-circle company_is_verified_true is-clickable" style={{ fontSize: '1em', marginLeft: '0.5em' }} />}
                        </label>
                        <div className="control">
                            <InputText
                                className="input"
                                type="text"
                                name="name"
                                maxLength={255}
                                value={selectedCompanyDefault?.name || ''}
                                onChange={handleInputChange}
                                placeholder="DeePsy"
                                disabled={isLoading || selectedCompany?.is_verified}
                            />
                        </div>
                    </div>
                </div>

                <div className="column">
                    <div className="field">
                        <label className="label">{t('website')}</label>
                        <div className="control">
                            <InputText
                                className="input"
                                type="text"
                                name="website"
                                maxLength={255}
                                value={selectedCompanyDefault?.website || ''}
                                onChange={handleInputChange}
                                placeholder="https://deepsy.com/"
                                pattern="^(https?:\/\/)?([\w\-]+(\.[\w\-]+)+)(\/[\w\-]*)*$"
                                disabled={isLoading}
                            />
                        </div>
                    </div>
                </div>
            </div>

            <div className="columns">
                <div className="column">
                    <div className="field">
                        <label className="label">{t('email')}</label>
                        <div className="control">
                            <InputText
                                className="input"
                                type="email"
                                name="email"
                                maxLength={100}
                                value={selectedCompanyDefault?.email || ''}
                                onChange={handleInputChange}
                                placeholder="email@deepsy.com"
                                disabled={isLoading}
                            />
                        </div>
                    </div>
                </div>

                <div className="column">
                    <div className="field">
                        <label className="label">{t('phoneNumber')}</label>
                        <div className="control">
                            <InputText
                                className="input"
                                type="text"
                                name="phone_number"
                                maxLength={20}
                                value={selectedCompanyDefault?.phone_number || ''}
                                onChange={(e) => {
                                    let value = e.target.value.replace(/[^\d+]/g, '');
                                    if (value[0] !== '+') value = '+' + value;
                                    handleInputChange(e)
                                }}
                                placeholder="+33412345678"
                                pattern="^\+?[1-9]\d{1,14}$"
                                disabled={isLoading}
                            />
                        </div>
                    </div>
                </div>
            </div>

            <div className="columns">
                <div className="column">
                    <div className="field">
                        <label className="label">{t('address')}</label>
                        <div className="control">
                            <InputText
                                className="input"
                                type="text"
                                name="address"
                                maxLength={255}
                                value={selectedCompanyDefault?.address || ''}
                                onChange={handleInputChange}
                                placeholder="55 Rue du Faubourg Saint-Honoré"
                                disabled={isLoading}
                            />
                        </div>
                    </div>
                </div>

                <div className="column">
                    <div className="field">
                        <label className="label">{t('city')}</label>
                        <div className="control">
                            <InputText
                                className="input"
                                type="text"
                                name="city"
                                maxLength={50}
                                value={selectedCompanyDefault?.city || ''}
                                onChange={handleInputChange}
                                placeholder="Paris"
                                disabled={isLoading}
                            />
                        </div>
                    </div>
                </div>
            </div>

            <div className="columns">

                <div className="column">
                    <div className="field">
                        <label className="label">{t('country')}
                            {!!selectedCompany?.is_verified && <i className="pi pi-info-circle company_is_verified_true is-clickable" style={{ fontSize: '1em', marginLeft: '0.5em' }} />}
                        </label>
                        <div className="control">
                            <select
                                className="input"
                                name="country"
                                value={selectedCompanyDefault?.country || ''}
                                onChange={handleInputChange}
                                disabled={isLoading || selectedCompany?.is_verified}
                            >
                                {Object.values(allCountries).map(country => (
                                    <option key={country} value={country}>
                                        {country}
                                    </option>
                                ))}
                            </select>
                        </div>
                    </div>
                </div>

                <div className="column">
                    <div className="field">
                        <label className="label">{t('postalCode')}</label>
                        <div className="control">
                            <InputText
                                className="input"
                                type="text"
                                name="postal_code"
                                maxLength={20}
                                value={selectedCompanyDefault?.postal_code || ''}
                                onChange={handleInputChange}
                                placeholder="75008"
                                disabled={isLoading}
                            />
                        </div>
                    </div>
                </div>
            </div>

            <div className="columns">
                <div className="column is-6">
                    <div className="field">
                        <label className="label">{t('industry')}</label>
                        <div className="control">
                            <InputText
                                className="input"
                                type="text"
                                name="industry"
                                maxLength={100}
                                value={selectedCompanyDefault?.industry || ''}
                                onChange={handleInputChange}
                                placeholder="Distribution"
                                disabled={isLoading}
                            />
                        </div>
                    </div>
                </div>
                <div className="column is-2">
                    <div className="field">
                        <label className="label">{t('size')}</label>
                        <div className="control">
                            <Dropdown
                                value={selectedCompanyDefault?.size || ''}
                                options={sizeOptions}
                                onChange={(e) => handleInputChange({ target: { name: 'size', value: e.value } })}
                                placeholder="Select Size"
                                disabled={isLoading}
                                panelClassName='primereact-dropdown-spaced'
                                className='primereact-dropdown-conventional'
                                style={{ borderRadius: 4 }}
                            />
                        </div>
                    </div>
                </div>
            </div>

            <h1 className="title is-5">Information légales</h1>

            <div className="columns">
                <div className="column">
                    <div className="field">
                        <label className="label">{t('legal_form')}
                            {!!selectedCompany?.is_verified && <i className="pi pi-info-circle company_is_verified_true is-clickable" style={{ fontSize: '1em', marginLeft: '0.5em' }} />}
                        </label>
                        <div className="control">
                            <InputText
                                className="input"
                                type="text"
                                name="legal_form"
                                maxLength={255}
                                value={selectedCompanyDefault?.legal_form || ''}
                                onChange={handleInputChange}
                                placeholder="SARL, SAS ..."
                                disabled={isLoading || selectedCompany?.is_verified}
                            />
                        </div>
                    </div>
                </div>

                <div className="column">
                    <div className="field">
                        <label className="label">{t('vat_number')}
                            {!!selectedCompany?.is_verified && <i className="pi pi-info-circle company_is_verified_true is-clickable" style={{ fontSize: '1em', marginLeft: '0.5em' }} />}
                        </label>
                        <div className="control">
                            <InputText
                                className="input"
                                type="text"
                                name="vat_number"
                                maxLength={255}
                                value={selectedCompanyDefault?.vat_number || ''}
                                onChange={handleInputChange}
                                placeholder="FR12345678901"
                                disabled={isLoading || selectedCompany?.is_verified}
                            />
                        </div>
                    </div>
                </div>
            </div>

            {selectedCompany?.country === 'France' && (
                <div className="columns">
                    <div className="column">
                        <div className="field">
                            <label className="label">{t('siren_number')}
                                {!!selectedCompany?.is_verified && <i className="pi pi-info-circle company_is_verified_true is-clickable" style={{ fontSize: '1em', marginLeft: '0.5em' }} />}
                            </label>
                            <div className="control">
                                <InputText
                                    className="input"
                                    type="text"
                                    name="siren_number"
                                    maxLength={255}
                                    value={selectedCompanyDefault?.siren_number || ''}
                                    onChange={handleInputChange}
                                    placeholder="922075494"
                                    disabled={isLoading || selectedCompany?.is_verified}
                                />
                            </div>
                        </div>
                    </div>

                </div>
            )}

            {selectedCompany?.country !== 'France' && (
                <div className="columns">
                    <div className="column">
                        <div className="field">
                            <label className="label">{t('universal_company_id')}
                                {!!selectedCompany?.is_verified && <i className="pi pi-info-circle company_is_verified_true is-clickable" style={{ fontSize: '1em', marginLeft: '0.5em' }} />}
                            </label>
                            <div className="control">
                                <InputText
                                    className="input"
                                    type="text"
                                    name="universal_company_id"
                                    maxLength={255}
                                    value={selectedCompanyDefault?.universal_company_id || ''}
                                    onChange={handleInputChange}
                                    placeholder="CNR, EIN, UID, Fiscal code etc..."
                                    disabled={isLoading || selectedCompany?.is_verified}
                                />
                            </div>
                        </div>
                    </div>

                </div>
            )}

            <button className={clsx("button is-primary is-light is-outlined", { "is-loading": isUpdating })} onClick={handleUpdate} disabled={isLoading}>
                {t('save')}
            </button>

            <h1 className="title mb-6 mt-6">{t('logo')}</h1>

            <div className="columns is-vcentered">
                <div className="column is-3">
                    <div
                        className="image is-400x400 is-rounded"
                        style={{ position: 'relative', cursor: 'pointer' }}
                        onDrop={handleDrop}
                        onDragOver={handleDragOver}
                        onDragLeave={handleDragLeave}>

                        {isLoading ? (
                            <ProgressSpinner style={{ width: '100%', height: '100%' }} strokeWidth="8" />
                        ) : (
                            <img
                                className="radius-20"
                                src={uploadedImage}
                                alt="Company Logo"
                                style={{ width: '100%', height: '100%', objectFit: 'cover', objectPosition: 'center' }}
                            />
                        )}

                        {isUploading && (
                            <div
                                style={{
                                    position: 'absolute',
                                    top: '50%',
                                    left: '50%',
                                    transform: 'translate(-50%, -50%)'
                                }}
                            >
                                <ProgressSpinner style={{ width: '50px', height: '50px' }} strokeWidth="8" />
                            </div>
                        )}
                    </div>

                    {isUploading && (
                        <div style={{ textAlign: 'center', marginTop: '10px' }}>
                            {t('sending_from')} <strong>{uploadDetails}</strong>
                        </div>
                    )}
                </div>

                <div className="column">
                    <div className="control">
                        <FileUpload
                            name="avatar"
                            mode="basic"
                            accept="image/*"
                            maxFileSize={2 * 1024 * 1024}  // 2MB
                            auto
                            chooseLabel={t('import_files')}
                            disabled={isUploading || isLoading}
                            customUpload
                            uploadHandler={(e) => {
                                const file = e.files[0];
                                if (file) handleFileUpload(file);
                            }}
                        />
                    </div>
                </div>
            </div>
        </div>
    );
};

export default GeneralInfo;